import React, { useState } from 'react';
import {
  Box, Button, Autocomplete, TextField, Avatar, Typography, Alert,
  CircularProgress, useMediaQuery,
} from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

// Cloudinary face-centered thumbnail
const photoThumb = (url, size = 64) =>
  url && typeof url === 'string' && url.includes('/upload/')
    ? url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,g_face/`)
    : url;

const parentHint = (person) => {
  const mother = person.mother?.name;
  const father = person.father?.name;
  if (mother && father) return `${father} & ${mother}`;
  if (father) return `Father: ${father}`;
  if (mother) return `Mother: ${mother}`;
  return null;
};

const PersonPicker = ({ label, value, onChange, people, disabled }) => (
  <Autocomplete
    fullWidth
    options={people}
    value={value}
    onChange={(e, v) => onChange(v)}
    getOptionLabel={(option) => option.name || ''}
    isOptionEqualToValue={(option, val) => option._id === val._id}
    autoHighlight
    blurOnSelect
    disabled={disabled}
    // Keep the dropdown fast on low-end phones: show the best 30 matches
    filterOptions={(options, { inputValue }) => {
      const q = inputValue.trim().toLowerCase();
      const matches = q
        ? options.filter((o) => o.name && o.name.toLowerCase().includes(q))
        : options;
      return matches.slice(0, 30);
    }}
    renderOption={(props, option) => (
      <Box component="li" {...props} key={option._id} sx={{ display: 'flex', gap: 1.5, py: 1 }}>
        <Avatar
          src={option.photoPath ? photoThumb(option.photoPath) : undefined}
          alt={option.name}
          sx={{ width: 36, height: 36 }}
        >
          {(option.name || '?').charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{option.name}</Typography>
          {parentHint(option) && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {parentHint(option)}
            </Typography>
          )}
        </Box>
      </Box>
    )}
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder="Type a name…" />
    )}
  />
);

const SearchPerson = ({ people, onRelationship, loading }) => {
  const [firstPerson, setFirstPerson] = useState(null);
  const [secondPerson, setSecondPerson] = useState(null);
  const [error, setError] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleRelationshipSearch = () => {
    setError('');
    if (loading) return setError('The family data is still loading — one moment.');
    if (!firstPerson || !secondPerson) return setError('Please choose both people.');
    if (firstPerson._id === secondPerson._id) return setError('Please choose two different people.');
    onRelationship({ firstPersonId: firstPerson._id, secondPersonId: secondPerson._id });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5 }}>
        <PersonPicker
          label="First person"
          value={firstPerson}
          onChange={(v) => { setFirstPerson(v); setError(''); }}
          people={people}
          disabled={loading}
        />
        <PersonPicker
          label="Second person"
          value={secondPerson}
          onChange={(v) => { setSecondPerson(v); setError(''); }}
          people={people}
          disabled={loading}
        />
      </Box>

      {error && <Alert severity="warning">{error}</Alert>}

      <Button
        variant="contained"
        size="large"
        startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <FamilyRestroomIcon />}
        onClick={handleRelationshipSearch}
        disabled={loading || !firstPerson || !secondPerson}
        sx={{
          alignSelf: isMobile ? 'stretch' : 'flex-start',
          minHeight: 48,
          px: 3,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 700,
          color: '#fff',
          background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)',
          '&:hover': { background: '#047857' },
          '&.Mui-disabled': {
            background: '#A7D9C4',
            color: '#ffffff',
          },
        }}
      >
        Find the relationship
      </Button>
    </Box>
  );
};

export default SearchPerson;
